import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as EditIcon } from '@repo/common/assets/icons/16px/icon_16px_edit_outline.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/16px/icon_16px_delete_outline.svg';
import { ReactComponent as InvisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_invisible-open_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_visible_outline.svg';

import {
  BaseButton,
  Modal,
  useAuth,
  useIsMobile,
  useT,
  FilterBottomSheet,
} from '@repo/common';
import AskQuestionModal from '../Qna/Modal/AskQuestionModal';
import AnswerModal from '../Qna/Modal/AnswerModal';

import {
  deleteQuestion,
  hideQuestion,
  unhideQuestion,
  updateOnlyTutor,
} from '../../api/qna/qna.api';

/**
 * OptionList 컴포넌트
 *
 * @component
 * @param {object} props - 컴포넌트 속성
 * @param {boolean} props.isMyCard - 현재 사용자의 카드인지 여부 (옵션 목록 변경)
 * @param {function} props.onEditClick - 'Edit Question' 클릭 핸들러
 * @param {function} props.onRemoveClick - 'Remove question' 클릭 핸들러
 * @param {function} props.onReportClick - 'Report' 클릭 핸들러
 */

function OptionList({
  isMyCard,
  data,
  onClose,
  onUpdate,
  anchorRect,
  onDeleteSuccess,
  isOnlyTutor,
}) {
  const listRef = useRef(null);
  const { user } = useAuth();
  const t = useT();
  const isMobile = useIsMobile();
  const [activeItem, setActiveItem] = useState(null);
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [isAskModalOpen, setIsAskModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [answerData, setAnswerData] = useState(data.qnaAnswer);
  const [isHidden, setIsHidden] = useState(data.hidden);

  const userRole = user?.role ?? null;
  const isAdmin = userRole === 'A';
  const isWideMenu = isAdmin || isMyCard;

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    const handleScroll = () => {
      onClose();
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  const isAnyModalOpen =
    isAnswerModalOpen ||
    isAskModalOpen ||
    isDeleteModalOpen ||
    isReportModalOpen;

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onClose();
  };

  const handleItemClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveItem(action);

    if (action === 'editAnswer') handleEditAnswer();
    if (action === 'hide') handleToggleVisibility();
    if (action === 'remove') handleRemove();
    if (action === 'edit') handleAskQuestion();
    if (action === 'onlyTutor') handleToggleOnlyTutor();
    if (action === 'report') handleReport();
  };

  const handleEditAnswer = () => {
    setIsAnswerModalOpen(true);
  };

  const closeAnswerModal = () => {
    setIsAnswerModalOpen(false);
    onClose();
  };

  const handleAskQuestion = async () => {
    setIsAskModalOpen(true);
  };

  const handleToggleVisibility = async () => {
    if (!data) return;

    let newHiddenState;
    try {
      if (isHidden) {
        await unhideQuestion(data.id);
        newHiddenState = false;
      } else {
        await hideQuestion(data.id);
        newHiddenState = true;
      }
      setIsHidden(newHiddenState);
      if (onUpdate) onUpdate('hidden', newHiddenState);
    } catch (error) {
      console.error('QnA 숨김/숨김 해제 실패:', error);
    }
  };

  const handleToggleOnlyTutor = async () => {
    try {
      const newOnlyTutor = !isOnlyTutor;
      await updateOnlyTutor(data.id, newOnlyTutor);
      onUpdate('onlyTutor', newOnlyTutor);
      onClose();
    } catch (error) {
      console.error('QnA onlyTutor 업데이트 실패:', error);
    }
  };

  const closeAskModal = () => {
    setIsAskModalOpen(false);
    onClose();
  };

  const handleRemove = async () => {
    setIsDeleteModalOpen(true);
  };

  const handleCancelButton = () => {
    setIsDeleteModalOpen(false);
    setIsReportModalOpen(false);
    onClose();
  };

  const handleDeleteQuestion = async () => {
    if (!data) {
      return;
    }

    try {
      await deleteQuestion(data.id);
      setIsDeleteModalOpen(false);
      onClose();

      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      console.error('QnA 삭제 실패:', error);
      setIsDeleteModalOpen(false);
    }
  };

  const handleReport = async () => {
    setIsReportModalOpen(true);
  };

  const leftPosition = isWideMenu
    ? anchorRect.right - 200
    : anchorRect.right - 100;

  const menuList = (
    <ul role='menu' className='list-none p-0 m-0'>
      {isAdmin && (
        <>
          <li className='flex items-center rounded-lg transition-colors duration-150 w-full tabletH:w-[164px]'>
            <BaseButton
              type='text'
              size='m'
              hideIcon
              onClick={(e) => handleItemClick(e, 'editAnswer')}
              width='100%'
              separate={true}
              leftalign={true}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'edit'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {t('Edit answer', '답변 수정')}
            </BaseButton>
          </li>

          <li className='flex items-center rounded-lg transition-colors duration-150'>
            <BaseButton
              type='text'
              size='m'
              hideIcon
              onClick={(e) => handleItemClick(e, 'hide')}
              width='100%'
              separate={true}
              leftalign={true}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'edit'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {isHidden
                ? t('Unhide (from user)', '사용자에게 다시 공개')
                : t('Hide (from user)', '사용자에게 숨기기')}
            </BaseButton>
          </li>

          <li className='flex items-center rounded-lg transition-colors duration-150'>
            <BaseButton
              type='text'
              size='m'
              hideIcon
              onClick={(e) => handleItemClick(e, 'remove')}
              width='100%'
              separate={true}
              leftalign={true}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'remove'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {t('Delete question', '질문 삭제')}
            </BaseButton>
          </li>
        </>
      )}

      {!isAdmin && isMyCard && (
        <>
          <li className='flex items-center rounded-lg transition-colors duration-150 w-full'>
            <BaseButton
              type='text'
              size='m'
              iconPosition='after'
              icon={<EditIcon />}
              onClick={(e) => {
                handleItemClick(e, 'edit');
              }}
              width='100%'
              separate={true}
              leftalign={true}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'edit'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {t('Edit Question', '질문 수정')}
            </BaseButton>
          </li>

          <li className='flex items-center rounded-lg transition-colors duration-150'>
            <BaseButton
              type='text'
              size='m'
              iconPosition='after'
              icon={<DeleteIcon />}
              onClick={(e) => handleItemClick(e, 'remove')}
              width='100%'
              separate={true}
              leftalign={true}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'remove'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {t('Remove question', '질문 삭제')}
            </BaseButton>
          </li>

          <li className='flex flex-wrap rounded-lg transition-colors duration-150 w-full tabletH:max-w-[200px]'>
            <BaseButton
              type='text'
              size='m'
              iconPosition='after'
              icon={
                isOnlyTutor ? (
                  <VisibleIcon className='w-5 h-5' />
                ) : (
                  <InvisibleIcon className='w-5 h-5' />
                )
              }
              onClick={(e) => handleItemClick(e, 'onlyTutor')}
              width='100%'
              separate={true}
              leftalign={true}
              bgclass={`tabletH:h-[60px] bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'edit'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='text-start typo-b14Strong break-words whitespace-normal'
            >
              {isOnlyTutor
                ? t('Make visible to everyone', '모두에게 공개')
                : t('Make visible to tutors only', '튜터에게만 공개')}
            </BaseButton>
          </li>
        </>
      )}

      {!isAdmin && !isMyCard && (
        <li className='flex items-center rounded-lg transition-colors duration-150'>
          <BaseButton
            type='text'
            size='m'
            hideIcon
            onClick={(e) => handleItemClick(e, 'report')}
            width='100%'
            bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
              activeItem === 'report'
                ? 'bg-grayscale-gray02 text-text-primary'
                : ''
            }`}
            leftalign={true}
            textclass='typo-b14Strong'
            hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
            activeclass='active:bg-grayscale-gray02 active:text-text-primary'
          >
            {t('Report', '신고')}
          </BaseButton>
        </li>
      )}
    </ul>
  );

  return ReactDOM.createPortal(
    <>
      {!isAnyModalOpen &&
        (isMobile ? (
          <FilterBottomSheet onClose={onClose}>{menuList}</FilterBottomSheet>
        ) : (
          <>
            <div
              className='fixed inset-0 z-[9999999] bg-transparent cursor-default'
              onClick={handleOverlayClick}
              onMouseDown={(e) => e.stopPropagation()}
            />
            <div
              ref={listRef}
              onMouseDownCapture={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: anchorRect.bottom + 8,
                left: leftPosition,
              }}
              className={`
              ${isMyCard ? 'w-[200px]' : 'min-w-[100px]'}
              bg-grayscale-gray00
              border border-grayscale-gray02
              rounded-lg
              shadow-popup-04
              p-[2px]
              z-[99999999]`}
            >
              <div className='soft-60 bg-grayscale-gray00 rounded-lg p-2'>
                {menuList}
              </div>
            </div>
          </>
        ))}

      <div className='z-[99999999]'>
        {isAnswerModalOpen && (
          <AnswerModal
            isOpen={isAnswerModalOpen}
            onClose={closeAnswerModal}
            qnaId={data.id}
            initialData={answerData}
            onAnswerUpdated={(updatedAnswer) => {
              setAnswerData(updatedAnswer);
              if (onUpdate) onUpdate(data.id, updatedAnswer);
            }}
          />
        )}
        {isDeleteModalOpen && (
          <Modal
            isOpen
            onClick={(e) => handleModalClick(e)}
            onClickLeftBtn={handleCancelButton}
            onClickRightBtn={handleDeleteQuestion}
            title={t('Delete this question?', '이 질문을 삭제할까요?')}
            subText={t(
              'This action can’t be undone',
              '이 작업은 취소할 수 없습니다',
            )}
            buttonSize='xl'
            leftButtonLabel={t('cancel', '취소')}
            leftHideIcon={true}
            rightButtonLabel={t('Delete', '삭제')}
            rightHideIcon={true}
            type='warning'
          />
        )}

        {isAskModalOpen && (
          <AskQuestionModal
            isOpen={isAskModalOpen}
            onClose={closeAskModal}
            initialData={{
              id: data.id,
              categoryId: data.category.id,
              title: data.title,
              content: data.content,
              tutorOnly: data.tutorOnly,
            }}
            onClick={(e) => handleModalClick(e)}
          />
        )}

        {isReportModalOpen && (
          <Modal
            isOpen
            onClick={(e) => handleModalClick(e)}
            onClickLeftBtn={handleCancelButton}
            title={t('Report this question?', '이 질문을 신고할까요?')}
            subText={t(
              'We’ll review this to keep Koready safe',
              'Koready의 안전을 위해 검토하겠습니다',
            )}
            buttonSize='xl'
            leftButtonLabel={t('cancel', '취소')}
            leftHideIcon={true}
            rightButtonLabel={t('Report', '신고')}
            rightHideIcon={true}
            type='warning'
          />
        )}
      </div>
    </>,
    document.body,
  );
}

export default OptionList;
